import { useEffect, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { pushModalLayer } from '../../lib/modalLayer';

interface ModalProps {
  title: string;
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  wide?: boolean;
  extraWide?: boolean;
  orderDetail?: boolean;
  fullScreen?: boolean;
  compactHeader?: boolean;
  /** Hide the header × close control (Escape still calls onClose). */
  hideClose?: boolean;
  /** Optional controls immediately after the title (e.g. Change file). */
  titleExtra?: ReactNode;
  /** Optional controls on the right side of the header (e.g. timer, Edit). */
  headerActions?: ReactNode;
  panelClassName?: string;
  panelStyle?: CSSProperties;
  /** Stack above an already-open modal and consume Escape first. */
  nested?: boolean;
}

export function Modal({
  title,
  open,
  onClose,
  children,
  wide,
  extraWide,
  orderDetail,
  fullScreen,
  compactHeader,
  hideClose,
  titleExtra,
  headerActions,
  panelClassName,
  panelStyle,
  nested = false,
}: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const popLayer = pushModalLayer();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (nested) e.stopImmediatePropagation();
      onClose();
    };
    window.addEventListener('keydown', onKey, nested);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey, nested);
      document.body.style.overflow = prev;
      popLayer();
    };
  }, [open, onClose, nested]);

  if (!open) return null;

  return createPortal(
    <div
      className={['modal-backdrop', nested ? 'modal-backdrop--nested' : ''].filter(Boolean).join(' ')}
      role="presentation"
    >
      <div
        className={[
          'modal-panel',
          wide ? 'modal-panel--wide' : '',
          extraWide ? 'modal-panel--dispatch' : '',
          orderDetail ? 'modal-panel--order-detail' : '',
          fullScreen ? 'modal-panel--fullscreen' : '',
          compactHeader ? 'modal-panel--compact-header' : '',
          panelClassName ?? '',
        ]
          .filter(Boolean)
          .join(' ')}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        style={panelStyle}
      >
        <header className="modal-header">
          <div className="modal-header__lead">
            <h2 id="modal-title">{title}</h2>
            {titleExtra ? <div className="modal-header__title-extra">{titleExtra}</div> : null}
          </div>
          <div className="modal-header__actions">
            {headerActions}
            {!hideClose ? (
              <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
                ×
              </button>
            ) : null}
          </div>
        </header>
        <div className="modal-body">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
